/**
 * PATH: packages/document-editor/src/useDocumentEditorFrame.ts
 * FILE_ID: DOCEDITOR-FRAME-HOOK-001
 * PURPOSE: Own frame state, live preview state, page settings, and pagination.
 * WHY: DocumentEditor remains a declarative frame over one TipTap instance.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { FileRecordReference, PageSettings, VersionedDocument } from './contracts'
import { createDocument } from './contracts'
import type { DocumentExtensionOptions } from './extensions'
import { buildDocumentExtensions } from './extensions'
import { exportDocumentHtml } from './serialization'
import { useDocumentEditor, type DocumentEditorChange } from './useDocumentEditor'
import { useDocumentPagination } from './useDocumentPagination'

export interface ExactPdfPreviewRequest {
  contract: 'exact-pdf-v1'
  document: VersionedDocument
}

export interface DocumentEditorProps {
  document: VersionedDocument
  onChange?: (change: DocumentEditorChange) => void
  extensionOptions?: DocumentExtensionOptions
  onSelectImage?: () => Promise<FileRecordReference | null>
  onOpenExactPdfPreview?: (request: ExactPdfPreviewRequest) => void
  domainToolbarActions?: React.ReactNode
  readOnly?: boolean
  loading?: boolean
  error?: string | null
  ariaLabel?: string
}

export function useDocumentEditorFrame(props: DocumentEditorProps) {
  const {
    ariaLabel,
    document: sourceDocument,
    extensionOptions,
    onChange,
    onOpenExactPdfPreview,
    readOnly,
  } = props
  const [zoom, setZoom] = useState(100)
  const [activePage, setActivePage] = useState(1)
  const [pageSettings, setPageSettings] = useState(sourceDocument.metadata.pageSettings)
  useEffect(() => {
    setPageSettings(sourceDocument.metadata.pageSettings)
  }, [sourceDocument.metadata.pageSettings])

  const liveDocument = useMemo(() => ({
    ...sourceDocument,
    metadata: { ...sourceDocument.metadata, pageSettings },
  }), [pageSettings, sourceDocument])
  const editor = useDocumentEditor({
    document: liveDocument,
    extensionOptions,
    editable: !readOnly,
    ariaLabel,
    onChange,
  })
  const pagination = useDocumentPagination(editor, pageSettings, zoom, setActivePage)
  const currentDocument = useCallback((settings: PageSettings = pageSettings) => createDocument(
    editor?.getJSON() ?? sourceDocument.content,
    { ...sourceDocument.metadata, pageSettings: settings },
  ), [editor, pageSettings, sourceDocument.content, sourceDocument.metadata])

  const changePageSettings = useCallback((settings: PageSettings) => {
    setPageSettings(settings)
    if (!editor || !onChange) return
    const document = currentDocument(settings)
    onChange({
      document,
      html: exportDocumentHtml(document, {
        extensions: buildDocumentExtensions(extensionOptions),
      }),
    })
  }, [currentDocument, editor, extensionOptions, onChange])

  const openExactPdfPreview = useCallback(() => {
    onOpenExactPdfPreview?.({
      contract: 'exact-pdf-v1',
      document: currentDocument(),
    })
  }, [currentDocument, onOpenExactPdfPreview])

  return {
    activePage,
    changePageSettings,
    editor,
    openExactPdfPreview,
    pageSettings,
    pagination,
    setZoom,
    zoom,
  }
}
